/**
 * The duplicate-invoice warning (TASK-0232, FR-DUP), shared by the import review, Record invoice
 * and the Receive tab.
 *
 * When an invoice number is typed or read, ask the API whether the same vendor already has a live
 * order carrying it. If so, say which order and ask what this is: a different delivery, more of the
 * same invoice (an add-on), or stop. Nothing here blocks anything: the primary action is never held
 * for the lookup or for an answer. An answer is an annotation: when one is chosen, one line goes into
 * the notes the screen already sends ("Invoice 70185 also on PO #12: add-on pages" / "...: different
 * delivery"); with no answer the action proceeds exactly as it did before and adds nothing. INC-8b
 * turns the same question into a hard check with the same `invoiceKey()`.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api/client';
import { Button } from '@/ui/Button';
import { Callout } from '@/ui/Callout';
import { useDebouncedValue } from '@/ui/MultiSelect';
import { money } from '@/lib/format';
import { INVOICE_NUMBER_MAX_LENGTH, invoiceKey } from '@kk-pos/shared/purchasing';
import { invoiceDuplicateKeys } from './api';
import type { InvoiceDuplicate } from './types';

export type DuplicateChoice = 'different' | 'addon';

export type DuplicateCheck = {
  /**
   * idle: no vendor or nothing to compare; checking: waiting for the answer; clear: no other order
   * has it; found: at least one does; unavailable: the lookup failed (never holds anything up).
   */
  state: 'idle' | 'checking' | 'clear' | 'found' | 'unavailable';
  matches: InvoiceDuplicate[];
  choice: DuplicateChoice | null;
  setChoice: (choice: DuplicateChoice | null) => void;
  /** The line to add to the order's notes for the chosen answer; null when none. */
  noteLine: string | null;
};

const DEBOUNCE_MS = 400;

/**
 * Exactly what the order's notes will say for an answer: "Invoice 70185 also on PO #49053159:
 * add-on pages" or "...: different delivery". `poId` is the newest order carrying the number.
 */
export function duplicateNoteLine(
  choice: DuplicateChoice | null,
  invoiceNumber: string,
  poId: number
): string | null {
  const where = `Invoice ${invoiceNumber.trim()} also on PO #${poId}`;
  if (choice === 'addon') return `${where}: add-on pages`;
  if (choice === 'different') return `${where}: different delivery`;
  return null;
}

/** The notes to send: what the person typed, then the answer on its own line. */
export function notesWithDuplicateAnswer(notes: string, noteLine: string | null): string {
  return [notes.trim(), noteLine].filter((part) => part).join('\n');
}

export function useDuplicateInvoiceCheck({
  vendorId,
  invoiceNumber,
  excludePoId = null,
  enabled = true,
}: {
  vendorId: number | null;
  invoiceNumber: string;
  /** The order being edited, so it never warns about itself. */
  excludePoId?: number | null;
  /** False while the screen cannot act on the answer (for example an import already confirmed). */
  enabled?: boolean;
}): DuplicateCheck {
  const typed = invoiceNumber.trim();
  const debounced = useDebouncedValue(typed, DEBOUNCE_MS);
  // A number over the limit matches nothing; the field already says so.
  const comparable = (value: string) =>
    value.length <= INVOICE_NUMBER_MAX_LENGTH ? invoiceKey(value) : '';
  const typedKey = comparable(typed);
  const lookupKey = comparable(debounced);
  const active = enabled && vendorId !== null && typedKey !== '';

  const query = useQuery({
    queryKey: invoiceDuplicateKeys.check(vendorId ?? 0, lookupKey, excludePoId),
    queryFn: () =>
      api.get<InvoiceDuplicate[]>('/purchasing/invoice-duplicates', {
        params: { vendorId: vendorId ?? 0, invoiceNumber: debounced, excludePoId },
      }),
    enabled: enabled && vendorId !== null && lookupKey !== '',
    staleTime: 15_000,
    retry: false,
  });

  // The answer belongs to one vendor and one number: a different vendor or number starts over.
  const answerKey = `${vendorId ?? 0}|${typedKey}`;
  const [answer, setAnswer] = useState<{ key: string; choice: DuplicateChoice | null }>({
    key: '',
    choice: null,
  });
  const choice = answer.key === answerKey ? answer.choice : null;

  let state: DuplicateCheck['state'] = 'idle';
  if (active) {
    // Still inside the typing pause, or the answer has not come back yet.
    if (typedKey !== lookupKey || query.isPending) state = 'checking';
    else if (query.isError) state = 'unavailable';
    else state = (query.data?.length ?? 0) > 0 ? 'found' : 'clear';
  }
  const matches = state === 'found' ? (query.data ?? []) : [];

  return {
    state,
    matches,
    choice,
    setChoice: (next) => setAnswer({ key: answerKey, choice: next }),
    noteLine:
      state === 'found' && matches[0]
        ? duplicateNoteLine(choice, typed, matches[0].poId)
        : null,
  };
}

/** `Jul 14`, with the year only when it is not this year. */
export function shortDay(value: string, now: Date = new Date()): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' as const }),
  }).format(d);
}

/** Where an existing order stands, in the words used in the sentence "…is already on PO #12 (…)". */
export function duplicateStanding(match: InvoiceDuplicate): string {
  switch (match.status) {
    case 'received':
      return match.receivedAt ? `received ${shortDay(match.receivedAt)}` : 'received';
    case 'partially_received':
      return match.receivedAt
        ? `partly received, last ${shortDay(match.receivedAt)}`
        : 'partly received';
    case 'submitted':
      return 'sent, not received yet';
    case 'draft':
      return `a draft from ${shortDay(match.createdAt)}`;
    default:
      return match.status.replace(/_/g, ' ');
  }
}

/** A plain link that opens the order in a new tab, so the screen being filled in is left alone. */
function PoLink({ match }: { match: InvoiceDuplicate }) {
  return (
    <a
      href={`/purchasing/${match.poId}?mode=lines`}
      target="_blank"
      rel="noreferrer"
      className="text-signal-ink font-mono hover:underline"
    >
      PO #{match.poId}
    </a>
  );
}

/**
 * Shown once there is a match. `onStop` leaves the action behind (the screen decides what leaving
 * means); it is a button, never a gate. Renders a quiet line while checking or when the check could
 * not be made, and nothing otherwise.
 */
export function DuplicateInvoiceCallout({
  check,
  vendorName,
  invoiceNumber,
  stopLabel = 'Stop',
  onStop,
  note,
  className,
}: {
  check: DuplicateCheck;
  vendorName: string | null;
  invoiceNumber: string;
  stopLabel?: string;
  onStop: () => void;
  /** One more sentence for the screen to add (for example why one answer is likely). */
  note?: string;
  className?: string;
}) {
  const typed = invoiceNumber.trim();

  if (check.state === 'checking') {
    return (
      <p className="text-2xs text-ink-faint" role="status">
        Checking whether invoice {typed} is already on an order…
      </p>
    );
  }
  if (check.state === 'unavailable') {
    return (
      <p className="text-2xs text-ink-faint" role="status">
        Could not check whether invoice {typed} is already on an order. You can go on.
      </p>
    );
  }
  if (check.state !== 'found') return null;

  const [first, ...rest] = check.matches;
  if (!first) return null;
  const who = vendorName ? ` from ${vendorName}` : '';
  const choices: Array<{ value: DuplicateChoice; label: string }> = [
    { value: 'different', label: 'A different delivery' },
    { value: 'addon', label: 'More of the same invoice (add-on)' },
  ];

  return (
    <Callout
      tone="warn"
      icon={<AlertTriangle />}
      title={
        <span role="status">
          Invoice {typed}
          {who} is already on <PoLink match={first} /> ({duplicateStanding(first)})
        </span>
      }
      className={className}
    >
      <div className="space-y-2">
        {first.total > 0 || first.createdBy ? (
          <p className="text-2xs text-ink-faint">
            {countLines(first.lineCount)}
            {first.total > 0 ? `, ${money(first.total)}` : ''}
            {first.createdBy ? `, entered by ${first.createdBy}` : ''}.
          </p>
        ) : null}
        {rest.length > 0 ? (
          <p className="text-2xs">
            Also on{' '}
            {rest.map((match, index) => (
              <span key={match.poId}>
                {index > 0 ? ', ' : ''}
                <PoLink match={match} /> ({duplicateStanding(match)})
              </span>
            ))}
            .
          </p>
        ) : null}
        <p>What is this one? You can go on without saying.</p>
        {note ? <p className="text-2xs text-ink-faint">{note}</p> : null}
        <div
          className="flex flex-wrap items-center gap-1.5"
          role="group"
          aria-label="What is this invoice"
        >
          {choices.map((option) => (
            <Button
              key={option.value}
              size="xs"
              variant={check.choice === option.value ? 'primary' : 'default'}
              aria-pressed={check.choice === option.value}
              onClick={() =>
                check.setChoice(check.choice === option.value ? null : option.value)
              }
            >
              {option.label}
            </Button>
          ))}
          <Button size="xs" variant="ghost" onClick={onStop}>
            {stopLabel}
          </Button>
        </div>
        {check.noteLine ? (
          <p className="text-2xs text-ink-faint">
            The order&rsquo;s notes will say &ldquo;{check.noteLine}&rdquo;.
          </p>
        ) : null}
      </div>
    </Callout>
  );
}

function countLines(count: number): string {
  return `${count} ${count === 1 ? 'line' : 'lines'}`;
}
